import React from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';
import { Card } from '@onward-delivery/design/card';
import { AlertTriangle as ReviewIcon } from '@onward-delivery/design/icons';
import { Tooltip } from '@onward-delivery/design/tooltip';
import { colors, typography } from '@/styles';

import { TOKENS } from './blocks';
import {
    COMPACT_CARD,
    DayNumber,
    FillBar,
    FutureResourceCard,
    Meta,
    PastResourceCard,
    PercentBadge,
} from './calendarBlocks';
import { FILL_STATES } from './dayMetrics';

const DAY_FILL_TOOLTIP = 'Fullest ceiling on the day';
const REVIEW_TOOLTIP = 'Something on this day needs a look';

// The count only earns its place when the bar does not cover everything out that day — "7 of 7
// resources" is noise, and a bare "all 1 resources" is worse.
const contributorNote = (contributors, resources) =>
    contributors === resources ? 'every resource on the day' : `${contributors} of ${resources} resources on the day`;

const barTooltip = ({ ceiling, used, limit, percent, contributors }, resources) =>
    `${ceiling.label} — ${used.toLocaleString()} / ${limit.toLocaleString()} ${
        ceiling.short
    } (${percent}%) across ${contributorNote(contributors, resources)}`;

// Fixed, so every bar on the cell starts at the same x and the day reads as one chart rather than
// four unrelated rows. Sized to "pieces", the longest of the four.
const LABEL_WIDTH = '40px';

const barRow = css`
    align-items: center;
    flex-wrap: nowrap;
    gap: 6px;
    margin-bottom: 4px;
`;

// A ceiling nobody on the day configured keeps its row rather than vanishing, so the cell does not
// change height as the fleet's configuration changes — but muted, since there is nothing to read.
const UnconfiguredBar = ({ ceiling }) => (
    <Grid
        container
        direction="row"
        css={[
            barRow,
            css`
                opacity: 0.45;
            `,
        ]}
    >
        <Meta
            css={css`
                flex: 0 0 ${LABEL_WIDTH};
            `}
        >
            {ceiling.short}
        </Meta>
        <span
            css={css`
                flex: 1;
                min-width: 0;
                height: 4px;
                border-radius: 999px;
                background-color: ${TOKENS.border};
            `}
        />
        <Meta
            css={css`
                flex: 0 0 auto;
            `}
        >
            —
        </Meta>
    </Grid>
);

const CeilingBar = ({ bar, resources }) => {
    if (bar.state === FILL_STATES.UNCONFIGURED) {
        return <UnconfiguredBar ceiling={bar.ceiling} />;
    }

    return (
        <Tooltip content={barTooltip(bar, resources)} side="top">
            {/* Radix needs one real node to anchor to, and it has to be the row itself — wrapping
                the row in a span would break the flex track the bar stretches in. */}
            <Grid container direction="row" css={barRow}>
                <Meta
                    css={css`
                        flex: 0 0 ${LABEL_WIDTH};
                    `}
                >
                    {bar.ceiling.short}
                </Meta>
                {/* min-width: 0 so the bar shrinks with the cell instead of setting a floor on it. */}
                <span
                    css={css`
                        flex: 1;
                        min-width: 0;
                    `}
                >
                    <FillBar percent={bar.percent} size="sm" />
                </span>
                <Meta
                    css={css`
                        flex: 0 0 auto;
                        font-weight: 700;
                        ${bar.state === FILL_STATES.AVAILABLE ? '' : `color: ${TOKENS.warning};`}
                        ${bar.state === FILL_STATES.FULL ? `color: ${TOKENS.destructive};` : ''}
                    `}
                >
                    {bar.percent}%
                </Meta>
            </Grid>
        </Tooltip>
    );
};

// Rendered on every day, 0 / 0 / 0 included, so every cell keeps the same shape.
const DayCounts = ({ aggregate }) => (
    <Tooltip content="Resources / stops / pieces" side="top">
        {/* flex, not the default inline: an inline trigger centres on its line box rather than on
            the grid row, which left it sitting a pixel or two off the badge beside it. */}
        <span
            css={css`
                display: flex;
                align-items: center;
                min-width: 0;
            `}
        >
            <Meta
                css={css`
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                `}
            >
                {aggregate.resources} / {aggregate.stops} / {aggregate.pieces}
            </Meta>
        </span>
    </Tooltip>
);

const billingPad = css`
    padding: 4px 6px 6px;
`;

// One card, quartered. A big number over a small label reads at a glance from across a grid; the
// six-line list it replaces needed the reader to find the line that mattered.
//
// Fixed 2x2 rather than a row of four: a row that narrow makes each number a column barely wider
// than its own digits, and the labels ellipsise away to nothing.
//
// Every bucket renders, zeros included. Hiding the empty ones would reshape the grid day to day,
// which is the opposite of scannable — and "0 invoiced" is information.
const BucketCell = ({ label, count, last, bottom }) => (
    <div
        css={css`
            min-width: 0;
            padding: 4px 6px;
            text-align: center;
            /* The lines down the middle, drawn as cell borders so the grid needs no extra markup.
               Colour-agnostic black at 10% so it reads on green, yellow and red alike. */
            border-right: ${last ? 'none' : '1px solid rgba(0, 0, 0, 0.1)'};
            border-bottom: ${bottom ? 'none' : '1px solid rgba(0, 0, 0, 0.1)'};
            opacity: ${count > 0 ? 1 : 0.45};
        `}
    >
        <div
            css={css`
                ${typography.sansSerif}
                font-size: 15px;
                font-weight: 700;
                line-height: 1.1;
                color: #2b2b2b;
            `}
        >
            {count}
        </div>
        <Meta
            css={css`
                display: block;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
            `}
        >
            {label}
        </Meta>
    </div>
);

// Work that never completed is folded into "Not invoiced" by dayBillingBuckets, so the card is the
// whole story and there is no line under it.
const PastBilling = ({ buckets = [] }) => {
    if (!buckets.some(({ count }) => count > 0)) {
        return <Meta css={billingPad}>No orders to invoice</Meta>;
    }

    return (
        <div css={billingPad}>
            {/* Deliberately untoned. The per-resource cards below carry the day's colour, and
                repeating it on the summary made a month of cells read as a wall of red and yellow
                with nothing standing out. The numbers are the signal here. */}
            <Card
                className={COMPACT_CARD}
                css={css`
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    padding: 0 !important;
                    overflow: hidden;
                `}
            >
                {buckets.map(({ key, label, count }, i) => (
                    <BucketCell key={key} label={label} count={count} last={i % 2 === 1} bottom={i > 1} />
                ))}
            </Card>
        </div>
    );
};

// One card per resource, under the day summary, with no cap — the week view has the height for it
// and seeing every truck is the whole reason to switch views. The month grid does not render these:
// two of twenty trucks was what made it unusable.
//
// Read-only. These used to open the resource config dialog, which is now reached from the Fleet tab
// and nowhere else — so a click anywhere on the cell, these cards included, opens the day.
const ResourceList = ({ entries, isPast }) => (
    <div
        css={css`
            padding: 6px 6px 0;
            min-width: 0;
        `}
    >
        {entries.map((entry) =>
            isPast ? (
                <PastResourceCard
                    key={entry.resource.resource_id}
                    resource={entry.resource}
                    billingState={entry.billingState}
                />
            ) : (
                <FutureResourceCard
                    key={entry.resource.resource_id}
                    resource={entry.resource}
                    fill={entry.fill}
                    accepting={entry.accepting}
                />
            )
        )}
    </div>
);

// Cell chrome and the day-open click belong to the generic Calendar; this is contents only.
//
// Nothing in here is clickable in either layout. Per-truck detail lives in the side dialog the cell
// already opens, and the resource config dialog is the Fleet tab's alone.
function Day({ dayData, dayNumber, isToday, isPast, isCurrentMonth, showResources = false }) {
    const { entries, aggregate, ceilingRollup, percent, measured, buckets, needsReview } = dayData;

    return (
        <Grid
            container
            direction="column"
            css={css`
                flex: 1;
                flex-basis: 0;
                min-width: 0;
                padding: 4px 2px 12px;

                /* MUI's Grid container wraps, and in a wrapping column the line takes the widest
                   item's max-content width — one long line would then stretch the cell. */
                flex-wrap: nowrap;
            `}
        >
            {/* Grid, not flex: `1fr auto 1fr` centres the counts on the cell itself. With
                space-between they would sit off-centre, since the day number and the badge are
                different widths. padding-bottom is what separates the header from the bars. */}
            <div
                css={css`
                    display: grid;
                    /* minmax(0, …) on every track: a grid item's min-width defaults to its content,
                       so at a narrow window the three tracks refused to shrink, overflowed the cell
                       and the badge rode over the counts. Now the counts ellipsise instead. */
                    grid-template-columns: minmax(0, 1fr) minmax(0, auto) minmax(0, 1fr);
                    align-items: center;
                    gap: 6px;
                    flex: 0;
                    flex-basis: 0;
                    padding: 0 4px 8px;
                    min-width: 0;
                `}
            >
                {/* Fixed square — justify-self keeps the grid track from stretching it to an ellipse. */}
                <div
                    css={css`
                        display: flex;
                        justify-content: center;
                        align-items: center;
                        justify-self: start;
                        width: 30px;
                        height: 30px;
                        border-radius: 50%;
                        background: ${isToday ? colors.blues.primary : 'transparent'};
                    `}
                >
                    <DayNumber
                        css={css`
                            color: ${isToday
                                ? colors.white.primary
                                : isCurrentMonth
                                ? colors.greys[3]
                                : colors.greys[4]};
                        `}
                    >
                        {dayNumber}
                    </DayNumber>
                </div>

                <DayCounts aggregate={aggregate} />

                <span
                    css={css`
                        display: flex;
                        align-items: center;
                        justify-content: flex-end;
                        justify-self: end;
                        min-width: 0;
                    `}
                >
                    {isPast ? (
                        needsReview ? (
                            // The word "Review" cost more width than it earned in a cell this
                            // narrow. The same alert triangle the unsettled resource cards use, so
                            // the flag and the cards below it say it the same way — with the
                            // tooltip carrying the meaning the text used to.
                            <Tooltip content={REVIEW_TOOLTIP} side="top">
                                <span
                                    aria-label="Needs review"
                                    css={css`
                                        display: flex;
                                        align-items: center;
                                        color: ${TOKENS.warning};

                                        svg {
                                            width: 16px;
                                            height: 16px;
                                        }
                                    `}
                                >
                                    <ReviewIcon />
                                </span>
                            </Tooltip>
                        ) : null
                    ) : measured ? (
                        <Tooltip content={DAY_FILL_TOOLTIP} side="top">
                            <span>
                                <PercentBadge percent={percent}>{percent}%</PercentBadge>
                            </span>
                        </Tooltip>
                    ) : null}
                </span>
            </div>

            {isPast ? (
                <PastBilling buckets={buckets} />
            ) : (
                <div
                    css={css`
                        padding: 4px 6px 6px;
                        min-width: 0;
                    `}
                >
                    {/* Nothing scheduled means nothing to measure — four muted rows on an empty day
                        would be all noise and no signal. */}
                    {aggregate.resources === 0 ? null : (
                        // Contained, so the bars read as one block against the day's counts above
                        // rather than floating in the cell — and so present and past days carry
                        // the same shape.
                        <Card
                            className={COMPACT_CARD}
                            css={css`
                                min-width: 0;
                            `}
                        >
                            {ceilingRollup.map((bar) => (
                                <CeilingBar key={bar.ceiling.value} bar={bar} resources={aggregate.resources} />
                            ))}
                        </Card>
                    )}
                </div>
            )}

            {showResources && entries.length > 0 ? <ResourceList entries={entries} isPast={isPast} /> : null}
        </Grid>
    );
}

// Every cell (35 or 42 in a month) re-renders together on any store change, and each one now draws
// four bars. Its props are the memoised day object and four scalars, so this holds as long as the
// Context value does.
export default React.memo(Day);
